'use client';

import { UseFormReturn } from 'react-hook-form';
import { giftAidStartLabels } from '@/lib/giftaid/start-date-labels';
import type { SignupFormData } from './signup-wizard';

interface StepReviewProps {
  form: UseFormReturn<SignupFormData>;
}

export function StepReview({ form }: StepReviewProps) {
  const values = form.getValues();
  // Same source as the radio group on the Gift Aid step, so the summary
  // can never name a different tax year from the one that was clicked.
  const giftAidLabels = giftAidStartLabels();

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Review & Create</h2>
        <p className="text-sm text-gray-500">Check your details before creating your account</p>
      </div>

      {/* Charity Info */}
      <div className="rounded-lg border p-4 space-y-2">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Charity</h3>
        <dl className="space-y-1">
          <div className="flex justify-between">
            <dt className="text-sm text-gray-600">Name</dt>
            <dd className="text-sm font-medium">{values.charityName}</dd>
          </div>
          {values.charityNumber && (
            <div className="flex justify-between">
              <dt className="text-sm text-gray-600">Registration No.</dt>
              <dd className="text-sm font-medium">{values.charityNumber}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-sm text-gray-600">Subdomain</dt>
            <dd className="text-sm font-medium">{values.subdomain}.trusteekit.co.uk</dd>
          </div>
        </dl>
      </div>

      {/* Gift Aid */}
      <div className="rounded-lg border p-4 space-y-2">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Gift Aid</h3>
        <dl className="space-y-1">
          <div className="flex justify-between">
            <dt className="text-sm text-gray-600">Claiming since</dt>
            <dd className="text-sm font-medium">{giftAidLabels[values.giftAidStartDate]}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-sm text-gray-600">Community buildings</dt>
            <dd className="text-sm font-medium">{values.operatesCommunityBuildings ? 'Yes' : 'No'}</dd>
          </div>
        </dl>
      </div>

      {/* Account — email is hydrated from localStorage on the post-verify
          remount; if the link was clicked on a different device, we
          omit the row gracefully. Password isn't carried client-side
          past step 1 (it's hashed in the pending row), so we just
          show a "set on step 1" placeholder. */}
      <div className="rounded-lg border p-4 space-y-2">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Account</h3>
        <dl className="space-y-1">
          {values.adminName && (
            <div className="flex justify-between">
              <dt className="text-sm text-gray-600">Name</dt>
              <dd className="text-sm font-medium">{values.adminName}</dd>
            </div>
          )}
          {values.adminEmail && (
            <div className="flex justify-between">
              <dt className="text-sm text-gray-600">Email</dt>
              <dd className="text-sm font-medium">{values.adminEmail}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-sm text-gray-600">Password</dt>
            <dd className="text-sm text-gray-500 italic">set on step 1</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
